import { FC, useCallback, useMemo, useState } from "react";
import { LinearProgress, Stack } from "@mui/material";
import { K8SLiveUsageResourceGridToolbar } from "./toolbar/K8SLiveUsageResourceGridToolbar";
import { ResourcesMasterGrid } from "../../../common/resources-ag-grid/ResourcesMasterGrid";
import type { GridApi } from "ag-grid-community";
import { ResourceType } from "@/services/cloudchipr.api";
import { useAppSelector } from "@/store/hooks";
import { gridResourceDataLoadingSelector } from "@/store/resources-grid/selectors/resource-data/gridResourceDataLoadingSelector";
import { kubernetesLiveUsageMgmtSelectedClusterIdsSelector } from "@/store/kubernetes-live-usage-mgmt/selectors/store-selectors/kubernetesLiveUsageMgmtSelectedClusterIdsSelector";
import { kubernetesLiveUsageMgmtAppliedFiltersWithRegionsByResourceTypeSelector } from "@/store/kubernetes-live-usage-mgmt/selectors/filters/kubernetesLiveUsageMgmtAppliedFiltersWithRegionsByResourceTypeSelector";
import { RootState } from "@/store/store";

interface K8SLiveUsageResourceGridProps {
  resourceType: ResourceType;
  masterGridId: string;
}

export const K8SLiveUsageResourceGrid: FC<K8SLiveUsageResourceGridProps> = ({
  resourceType,
  masterGridId,
}) => {
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const accountIds = useAppSelector(
    kubernetesLiveUsageMgmtSelectedClusterIdsSelector,
  );

  const loading = useAppSelector((state) =>
    gridResourceDataLoadingSelector(state, masterGridId),
  );

  const resourceFiltersSelector = useCallback(
    (state: RootState) => {
      return kubernetesLiveUsageMgmtAppliedFiltersWithRegionsByResourceTypeSelector(
        state,
        resourceType,
      );
    },
    [resourceType],
  );

  const resourceFilters = useAppSelector(resourceFiltersSelector);

  const dataToFetchResources = useMemo(() => {
    return { accountIds: accountIds ?? [], resourceFilters };
  }, [accountIds, resourceFilters]);

  return (
    <Stack height="100%" width="100%">
      {loading && <LinearProgress />}

      <K8SLiveUsageResourceGridToolbar
        masterResourceType={resourceType}
        gridApi={gridApi}
        masterGridId={masterGridId}
      />

      <ResourcesMasterGrid
        gridApi={gridApi}
        key={resourceType}
        masterGridId={masterGridId}
        resourceType={resourceType}
        masterResourceType={resourceType}
        setGridApi={setGridApi}
        dataToFetchResources={dataToFetchResources}
      />
    </Stack>
  );
};
